import { supabase } from './supabase';

export type ResumeParseMode = 'fast' | 'standard' | 'ultimate';

export interface ResumeParseConfig {
  enabled: boolean;

  /**
   * Supabase Edge Function 地址（推荐同域）：
   * - "/functions/v1/parse-resume"
   * 或者完整URL：
   * - "https://<project-ref>.supabase.co/functions/v1/parse-resume"
   */
  function_url: string;

  mode?: ResumeParseMode;   // default: standard
  parse_avatar?: 0 | 1;     // default: 0
  timeout_ms?: number;      // default: 60000 (提升到60秒)
  retry?: number;           // default: 1
}

export interface QingsongParseResponse {
  status: { code: number; message: string };
  parser_info?: { version?: string; parse_time?: string };
  result?: any;
}

export interface ParsedResumeData {
  name?: string;
  phone?: string;
  email?: string;
  gender?: string;
  birth_date?: string;
  wechat?: string;
  linkedin_url?: string;
  location?: string;

  highest_education?: string;
  graduation_school?: string;
  major?: string;
  graduation_year?: number;

  current_company?: string;
  current_position?: string;
  current_salary?: string;

  expected_salary?: string;
  years_of_experience?: number;

  job_search_status?: string;
  available_date?: string;

  preferred_industry?: string[];
  preferred_position?: string;
  preferred_company_type?: string;

  work_mode_preference?: string;
  overtime_acceptance?: string;
  travel_acceptance?: string;

  certifications?: string[];
  self_evaluation?: string;

  language_skills?: Record<string, string>;
  education?: Array<{ school: string; degree: string; major: string; start_date: string; end_date: string }>;
  work_experience?: Array<{ company: string; position: string; start_date: string; end_date: string; description: string }>;

  // ✅ 保留原始 result，便于写入 supabase jsonb / 未来字段扩展
  raw?: any;
}

const LOCAL_STORAGE_KEY = 'resume_parse_active_config';

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function getExtFromName(name: string) {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : '';
}

function assertFileSupported(file: File) {
  // 你页面 accept=".pdf,doc,docx"；这里也允许 txt
  const ext = getExtFromName(file.name);
  const ok = ['pdf', 'doc', 'docx', 'txt'].includes(ext);
  if (!ok) throw new Error(`不支持的文件类型：.${ext}（仅支持 pdf/doc/docx/txt）`);
}

function assertFileSizeByMode(file: File, mode: ResumeParseMode) {
  // 文档：fast 6MB / standard 15MB / ultimate 20MB
  const mb = file.size / (1024 * 1024);
  const limitMb = mode === 'fast' ? 6 : mode === 'standard' ? 15 : 20;
  if (mb > limitMb) {
    throw new Error(`文件过大：${mb.toFixed(2)}MB，${mode} 模式上限 ${limitMb}MB`);
  }
}

function firstOf<T>(arr?: T[]): T | undefined {
  return Array.isArray(arr) && arr.length ? arr[0] : undefined;
}

function parseYearMonth(s?: string): { year?: number; month?: number } {
  if (!s) return {};
  const m = String(s).match(/(\d{4})\.(\d{1,2})/);
  if (!m) return {};
  return { year: Number(m[1]), month: Number(m[2]) };
}

function calcYearsOfExperience(careerList?: Array<{ start_date?: string; end_date?: string }>): number | undefined {
  if (!Array.isArray(careerList) || careerList.length === 0) return undefined;

  const starts = careerList
    .map(x => parseYearMonth(x.start_date))
    .filter(x => x.year);

  if (starts.length === 0) return undefined;

  const earliest = starts.reduce((a, b) => {
    if ((b.year! < a.year!) || (b.year === a.year && (b.month ?? 1) < (a.month ?? 1))) return b;
    return a;
  });

  const now = new Date();
  const endCandidates = careerList.map(x => {
    const end = String(x.end_date || '').trim();
    if (!end || end.includes('至今') || end.includes('目前')) {
      return { year: now.getFullYear(), month: now.getMonth() + 1 };
    }
    const ym = parseYearMonth(end);
    if (ym.year) return ym;
    return undefined;
  }).filter(Boolean) as Array<{year:number; month:number}>;

  const latest = endCandidates.length
    ? endCandidates.reduce((a, b) => ((b.year > a.year) || (b.year === a.year && b.month > a.month)) ? b : a)
    : { year: now.getFullYear(), month: now.getMonth() + 1 };

  const startMonthIndex = (earliest.year! * 12) + ((earliest.month ?? 1) - 1);
  const endMonthIndex = (latest.year * 12) + ((latest.month ?? 1) - 1);
  const months = Math.max(0, endMonthIndex - startMonthIndex);

  return Math.round((months / 12) * 10) / 10;
}

function mergeSkillsToCertifications(careerList: any[]): string[] {
  const set = new Set<string>();
  for (const c of careerList || []) {
    const skills = Array.isArray(c?.skills) ? c.skills : [];
    for (const s of skills) {
      const t = String(s || '').trim();
      if (t) set.add(t);
    }
  }
  return Array.from(set);
}

export function normalizeQingsongResponseToParsed(resp: QingsongParseResponse): ParsedResumeData {
  const r = resp?.result ?? {};
  const basic = r.basic_info ?? {};
  const contact = r.contact_info ?? {};
  const highestEdu = r.highest_edu_info ?? {};
  const workInfo = r.work_info ?? {};
  const expect0 = firstOf(r.expect_job_list) ?? {};
  const eduList = Array.isArray(r.edu_list) ? r.edu_list : [];
  const careerList = Array.isArray(r.career_list) ? r.career_list : [];
  const projectList = Array.isArray(r.project_list) ? r.project_list : [];
  const trainingList = Array.isArray(r.training_list) ? r.training_list : [];
  const practiceList = Array.isArray(r.practice_list) ? r.practice_list : [];
  const langCertList = Array.isArray(r.lang_cert_list) ? r.lang_cert_list : [];
  const certList = Array.isArray(r.cert_list) ? r.cert_list : [];
  const computerCertList = Array.isArray(r.computer_cert_list) ? r.computer_cert_list : [];
  const awardList = Array.isArray(r.award_list) ? r.award_list : [];
  const skillList = Array.isArray(r.skill_list) ? r.skill_list : [];
  const resumeContent = r.resume_content ?? {};
  const resumeInfo = r.resume_info ?? {};

  const phone = firstOf(contact.phone_list) ?? '';
  const email = firstOf(contact.email_list) ?? '';
  const graduationYear = parseYearMonth(highestEdu.date).year;

  const education = eduList.map((e: any) => ({
    school: e.school ?? '',
    degree: e.degree ?? '',
    major: e.major ?? '',
    minor: e.minor ?? '',
    dept: e.dept ?? '',
    start_date: e.start_date ?? '',
    end_date: e.end_date ?? '',
    location: e.loc ?? '',
    courses: Array.isArray(e.courses) ? e.courses : [],
    gpa: e.gpa ?? '',
    relative_rank: e.relative_rank ?? '',
    abs_rank: e.abs_rank ?? '',
    description: e.desc ?? '',
    tags: Array.isArray(e.tags) ? e.tags : [],
    school_info: e.school_info ?? {},
  })).filter((e: any) => e.school || e.degree || e.major);

  const work_experience = careerList.map((c: any) => ({
    company: c.company ?? '',
    position: c.position ?? '',
    position_type: c.position_type ?? '',
    nature: c.nature ?? '',
    dept: c.dept ?? '',
    start_date: c.start_date ?? '',
    end_date: c.end_date ?? '',
    duration: c.duration ?? '',
    salary: c.salary ?? '',
    location: c.loc ?? '',
    why_leave: c.why_leave ?? '',
    report_to: c.report_to ?? '',
    company_desc: c.company_desc ?? '',
    company_nature: c.company_nature ?? '',
    industry: c.industry ?? '',
    stuff_size: c.stuff_size ?? '',
    subordinate: c.subordinate ?? '',
    description: c.desc ?? '',
    skills: Array.isArray(c.skills) ? c.skills : [],
  })).filter((w: any) => w.company || w.position);

  const certifications = mergeSkillsToCertifications(careerList);
  
  // 提取所有技能
  const allCerts = [
    ...certList.map((c: any) => c.name ?? ''),
    ...computerCertList.map((c: any) => c.name ?? ''),
  ].filter(Boolean);

  // 提取技能列表
  const skills = skillList.map((s: any) => s.skill ?? '').filter(Boolean);

  // 提取语言能力
  const langSkills: Record<string, string> = {};
  langCertList.forEach((l: any) => {
    if (l.lang) {
      langSkills[l.lang] = l.cert ?? '';
    }
  });

  // 提取期望地点
  const preferredLocs = Array.isArray(expect0.locs) ? expect0.locs : [];

  return {
    // 基本信息
    name: basic.name ?? '',
    gender: basic.gender ?? '',
    phone,
    email,
    birth_date: basic.birthday ?? '',
    wechat: contact.wechat ?? '',
    linkedin_url: '',

    location: basic.city ?? workInfo.loc ?? basic.addr ?? '',

    // 教育背景
    highest_education: highestEdu.degree ?? '',
    graduation_school: highestEdu.school ?? '',
    major: highestEdu.major ?? '',
    graduation_year: graduationYear,

    // 工作信息
    current_company: workInfo.company ?? '',
    current_position: workInfo.position ?? '',
    current_salary: workInfo.salary ?? '',

    // 求职意向
    expected_salary: expect0.salary ?? '',
    preferred_industry: Array.isArray(expect0.industry) ? expect0.industry : [],
    preferred_position: expect0.jobs ? expect0.jobs.join('、') : '',
    preferred_company_type: '',

    years_of_experience: workInfo.work_year ?? calcYearsOfExperience(careerList),

    job_search_status: workInfo.status ? String(workInfo.status) : '',
    available_date: expect0.onboard_time ?? '',

    work_mode_preference: '',
    overtime_acceptance: '',
    travel_acceptance: '',

    certifications: [...certifications, ...allCerts],
    self_evaluation: resumeContent.self_evaluation ?? '',

    language_skills: langSkills,
    education,
    work_experience,

    // 保留完整的原始数据
    raw: {
      ...r,
      // 额外保存的字段
      basic_info: basic,
      contact_info: contact,
      highest_edu_info: highestEdu,
      work_info: workInfo,
      expect_job_list: r.expect_job_list,
      projects: projectList,
      trainings: trainingList,
      practices: practiceList,
      lang_certs: langCertList,
      awards: awardList,
      skills: skillList,
      resume_info: resumeInfo,
      resume_content: resumeContent,
    },
  };
}

function assertParseSuccess(json: any) {
  const code = json?.status?.code;
  if (code !== 200) {
    const msg = json?.status?.message || `解析失败，code=${code ?? 'unknown'}`;
    throw new Error(msg);
  }
}

async function fetchWithRetry(input: RequestInfo, init: RequestInit, retries: number) {
  let lastErr: any;
  for (let i = 0; i <= retries; i++) {
    try {
      const resp = await fetch(input, init);
      return resp;
    } catch (e) {
      lastErr = e;
      if (i < retries) await sleep(400 * (i + 1));
    }
  }
  throw lastErr;
}

export const resumeParseService = {
  async getActiveConfig(): Promise<ResumeParseConfig | null> {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!raw) return null;

      const cfg = JSON.parse(raw) as ResumeParseConfig;
      if (!cfg?.enabled) return null;
      if (!cfg.function_url) return null;

      return cfg;
    } catch {
      return null;
    }
  },

  async setActiveConfig(cfg: ResumeParseConfig) {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cfg));
  },

  /**
   * 解析简历（通过 Supabase Edge Function 代理）
   * Edge Function 接收 multipart/form-data：
   * - file: File
   * - mode: fast/standard/ultimate
   * - parse_avatar: 0/1
   */
  async parseResume(file: File, override?: Partial<ResumeParseConfig>): Promise<ParsedResumeData> {
    assertFileSupported(file);

    const baseCfg = await this.getActiveConfig();
    if (!baseCfg) throw new Error('未配置简历解析接口');

    const cfg: ResumeParseConfig = { ...baseCfg, ...override };
    const mode: ResumeParseMode = cfg.mode ?? 'standard';
    const parse_avatar: 0 | 1 = (cfg.parse_avatar ?? 0) as 0 | 1;
    const timeoutMs = cfg.timeout_ms ?? 60000; // 提升到60秒
    const retry = cfg.retry ?? 1;

    assertFileSizeByMode(file, mode);

    const form = new FormData();
    form.append('file', file);
    form.append('mode', mode);
    form.append('parse_avatar', String(parse_avatar));

    // 获取 Supabase 会话令牌
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
      console.log(`[简历解析] 开始解析: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB), 模式: ${mode}, 超时: ${timeoutMs}ms`);
      
      const headers: HeadersInit = {
        'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '',
      };

      // 如果用户已登录，添加授权令牌
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const resp = await fetchWithRetry(cfg.function_url, {
        method: 'POST',
        headers,
        body: form,
        signal: controller.signal,
      }, retry);

      if (!resp.ok) {
        // Edge Function 可能返回 text/json；这里尽量读出详细错误
        const text = await resp.text().catch(() => '');
        console.error('[简历解析] HTTP错误:', resp.status, text);
        throw new Error(text || `解析代理 HTTP 错误：${resp.status} ${resp.statusText}`);
      }

      const json = (await resp.json()) as QingsongParseResponse;
      assertParseSuccess(json);

      console.log('[简历解析] 解析成功');
      return normalizeQingsongResponseToParsed(json);
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        console.error('[简历解析] 超时:', timeoutMs);
        throw new Error(`解析超时（${timeoutMs / 1000}秒）。建议：\n1. 切换到 fast 模式\n2. 检查网络连接\n3. 尝试压缩文件大小`);
      }
      console.error('[简历解析] 失败:', e);
      throw e;
    } finally {
      window.clearTimeout(timer);
    }
  },
};
